/**
 * ============================================================================
 * SideButtons — Model (types + pure helpers)
 * ============================================================================
 * Source of the icons: v5 commonData.Section (root layout,
 * CommonDataContext) — each item's isEnabled decides whether it shows.
 * Normalised to SideButtonItem for the view.
 * ============================================================================
 */

export const SCHOLORSHIPS_PATHS = ["/"];

/** Tray shows once scrollY passes this fraction of the viewport (≈ hero height). */
export const HERO_SCROLL_RATIO = 0.7;

export const WHATTS_APP_PHONE =
  "https://api.whatsapp.com/send/?phone=919818795446&text=Hi&type=phone_number&app_absent=0&utm_source=WAEngChatBot";

export const WHATTS_APP_NUMBER_CHANGE_PROGRAMS = ["MBA", "BBA"];

/** v5 commonData.Section item (ComponentSharedSection). */
export interface SideButtonV5Raw {
  id?: string | null;
  sectionName?: string | null;
  isEnabled?: boolean | null;
  alt?: string | null;
  target?: string | null;
  url?: string | null;
  source?: { url?: string | null } | null;
}

export interface SideButtonItem {
  /** "call-button" | "whatsapp-link" — drives behaviour + GA. */
  title: string;
  url: string;
  alt: string;
  iconUrl: string;
  openInNewTab: boolean;
}

export interface SideButtonsProps {
  modalType?: any;
  pageType?: any;
  associatedPrograms?: any;
  isTestScholarshipEnabled?: boolean;
}

export interface SideButtonsViewModel {
  filterCallButton: SideButtonItem[];
  isVisible: boolean;
  isMobileView: boolean;
  isMobileViewDisabled: boolean;
  fireGtEvents: () => void;
  handleClick: (item: SideButtonItem) => void;
  renderItemUrl: (itemUrl: string, title: string) => string;
}

export function resolveWhatsAppUrl(
  itemUrl: string,
  title: string,
  associatedPrograms: any[] = [],
): string {
  if (
    WHATTS_APP_NUMBER_CHANGE_PROGRAMS.includes(
      associatedPrograms[0]?.attributes?.testimonialText
        ?.toLocaleUpperCase()
        .trim(),
    ) &&
    title == "whatsapp-link"
  ) {
    return WHATTS_APP_PHONE;
  }
  return itemUrl;
}

/** "tel:…" or a bare phone number ("+91 98765-43210"). */
export function isPhoneUrl(url?: string | null): boolean {
  const value = (url ?? "").trim();
  return /^tel:/i.test(value) || /^\+?[\d\s()-]{6,}$/.test(value);
}

/** Blank or phone url → the icon opens the RFI form instead of a link. */
export function opensRfiForUrl(url?: string | null): boolean {
  return !url?.trim() || isPhoneUrl(url);
}

/** "call button" / "Call-Button" → "call-button". */
function toSlug(value?: string | null): string {
  return (value ?? "").trim().toLowerCase().replace(/[\s_]+/g, "-");
}

/** v5 target ("_blank" / "blank" / "_self" / "self"); unset → external http(s) links open in a new tab (as v4 WhatsApp did). */
function opensInNewTab(target: string | null | undefined, url: string): boolean {
  const normalized = toSlug(target).replace(/^_/, "");
  if (normalized === "blank") return true;
  if (normalized === "self") return false;
  return /^https?:\/\//i.test(url);
}

/** Reads fetchCommonData()'s sideButtons (null → CMS has no Section yet / older cache entry). */
export function getSideButtonsV5(commonData: unknown): SideButtonV5Raw[] | null {
  const items = (commonData as { sideButtons?: unknown } | null | undefined)?.sideButtons;
  return Array.isArray(items) ? (items as SideButtonV5Raw[]) : null;
}

function fromV5(items: SideButtonV5Raw[]): SideButtonItem[] {
  return items
    .filter((item) => item?.isEnabled)
    .map((item) => {
      const url = item.url ?? "";
      return {
        title: toSlug(item.sectionName) || toSlug(item.alt),
        url,
        alt: item.alt ?? "",
        iconUrl: item.source?.url ?? "",
        openInNewTab: opensInNewTab(item.target, url),
      };
    });
}

/** v5 Section items; none when the CMS has no Section (no icons). */
export function resolveSideButtonItems(v5: SideButtonV5Raw[] | null | undefined): SideButtonItem[] {
  return Array.isArray(v5) ? fromV5(v5) : [];
}
